import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";

import { updateUserProfile } from "_firebase";

import { actions as userActions } from "store/users";

import { createOrganizationAPI } from "utils/organization/api";
import { checkValidity } from "utils/fields";

import Input from "components/Input";
import Button from "components/Button";

import classes from "../style.module.scss";

const { fetchUserData } = userActions;

const PeopleStep = ({ name, orgType, emails, setEmails, onBack, setAlert, onCreated }) => {
    const dispatch = useDispatch();
    const uid = useSelector(({ user }) => user.uid);
    const email = useSelector(({ user }) => user.email);
    const userName = useSelector(({ user }) => user.name);

    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);

    const emailList = emails
        .split(";")
        .map((email) => email.trim())
        .filter(Boolean);

    const handleChange = (value) => {
        setEmails(value);
        setError("");
    };

    const handleCreate = async () => {
        const invalidEmails = emailList.filter((email) => !checkValidity("Email", email, { isEmail: true }).isValid);

        if (invalidEmails.length) {
            setError(`Not a valid email address: ${invalidEmails.join(", ")}`);
            return;
        }

        setCreating(true);
        setError("");

        // DashboardAPI returns the error instead of throwing, so check the status
        const { status = 0, data: { orgId = "" } = {} } = await createOrganizationAPI({
            name,
            orgType,
            createdBy: uid,
            usersToInvite: emailList,
        });

        if (status !== 200 && status !== 201) {
            setError("Could not create the organization. Please try again.");
            setCreating(false);

            return;
        }

        await updateUserProfile(uid, { reqTeamToCreate: false });

        Sentry.withScope((scope) => {
            scope.setTransactionName(`New Organization ${name}`);
            scope.setLevel("info");
            scope.setUser({ email, name: userName, uid });
            scope.setTag("organization", "create");
            scope.setFingerprint(["new-organization", name]);
            Sentry.captureMessage("New Organization Created");
            scope.setExtras({
                organization: name,
                orgType,
                createdBy: uid,
                createdByEmail: email,
                invitesSent: emailList.length,
                invitedEmails: emailList,
            });
            const error = new Error();
            error.name = `New Organization ${name}`;
            error.message = `${userName || email} created organization ${name}. TYPE: ${orgType}. INVITES: ${emailList.join(", ") || "none"}.`;
            scope.captureException(error);
        });

        setCreating(false);
        setAlert({
            open: true,
            message: emailList.length ? `${name} created, invitations sent` : `${name} created`,
            anchorOrigin: { vertical: "top", horizontal: "center" },
            direction: "down",
            severity: "success",
        });

        // the new organization arrives with the user data; isOpen follows reqTeamToCreate when the
        // modal came from sign up, and onCreated closes it when it was opened by hand
        await dispatch(fetchUserData(uid));

        // the id lets the caller open the organization that was just created, instead of
        // leaving the page pointed at whichever one it was showing before
        onCreated?.(orgId);
    };

    return (
        <>
            <p>
                Invite people to {name}. Enter each email address separated by a semicolon, or skip this and add them
                later. Users within an organization can share models, code libraries, and upgrade libraries with each
                other.
            </p>
            <Input
                label="Emails"
                placeholder="name@company.com; name@company.com"
                meta={{ invalid: Boolean(error), error }}
                setTouched
                disabled={creating}
                input={{ value: emails, onChange: handleChange }}
            />
            <div className={classes.footer}>
                <Button onClick={onBack} type="hollow" disabled={creating}>
                    Back
                </Button>
                <Button onClick={handleCreate} disabled={creating}>
                    {creating ? "Creating..." : emailList.length ? "Create and Invite" : "Create"}
                </Button>
            </div>
        </>
    );
};

export default PeopleStep;
